'use client';

import React, { type ReactElement, useEffect, useMemo, useState } from 'react';

import { Drawer as AntDrawer, type DrawerProps as AntDrawerProps } from 'antd';
import classNames from 'classnames';
import { type SwipeEventData, useSwipeable } from 'react-swipeable';

export interface DrawerProps extends AntDrawerProps {
  onClose: () => void;
  height: string | number;
  noPadding?: boolean;
  rootClassName?: string;
}

const placementMapper = {
  bottom: 'Down',
  left: 'Left',
  right: 'Right',
  top: 'Up',
};

const placementClassMapper = {
  bottom: 'w-full h-12 top-0 right-0',
  left: 'h-full w-12 right-0 top-0',
  right: 'h-full w-12 left-0 top-0',
  top: 'w-full h-12 bottom-0 right-0',
};

export default function Drawer({
  className,
  children,
  placement,
  open,
  onClose,
  height,
  width,
  noPadding,
  rootClassName = '',
  ...rest
}: DrawerProps): ReactElement {
  const [openDrawer, setOpenDrawer] = useState(open);
  const [noTransition, setNoTransition] = useState(false);
  const [eventData, setEventData] = useState<SwipeEventData | null>(null);

  const drawerContentWrapper: any = useMemo(() => {
    if (typeof window !== 'undefined' && document)
      return document.getElementsByClassName('ant-drawer-content-wrapper')[0];
  }, [eventData]);

  const handlers = useSwipeable({
    onSwiped: (eventData) => {
      if (placement && placementMapper[placement] === eventData.dir) {
        onCloseHandler();
      }
    },
    onSwiping: (eventData) => {
      if (placement && placementMapper[placement] === eventData.dir) {
        setEventData(eventData);
      }
    },
    onSwipeStart: () => {
      setNoTransition(true);
    },
  });

  useEffect(() => {
    if (open !== openDrawer) {
      setOpenDrawer(open);
    }
  }, [open]);

  useEffect(() => {
    if (eventData && drawerContentWrapper) {
      if (placement === 'bottom' || placement === 'top') {
        drawerContentWrapper.style.height = `calc(${height}${
          typeof height === 'string' ? '' : 'px'
        } - ${eventData.absY}px)`;
      } else {
        drawerContentWrapper.style.width = `calc(${width}${
          typeof width === 'string' ? '' : 'px'
        } - ${eventData.absX}px)`;
      }
    }
  }, [eventData]);

  const setDrawerContentWrapperSize = () => {
    if (drawerContentWrapper) {
      if (placement === 'bottom' || placement === 'top') {
        drawerContentWrapper.style.height =
          typeof height === 'string' ? height : height + 'px';
      } else {
        drawerContentWrapper.style.width =
          typeof width === 'string' ? width : width + 'px';
      }
    }
  };

  const onCloseHandler = (): void => {
    setTimeout(() => {
      setDrawerContentWrapperSize();
    }, 500);

    setNoTransition(false);

    if (onClose) {
      onClose();
    }
  };

  return (
    <AntDrawer
      closeIcon={false}
      className={classNames(className)}
      placement={placement}
      {...rest}
      onClose={onCloseHandler}
      height={height}
      width={width}
      open={openDrawer}
      key={placement?.toString()}
      rootClassName={classNames('drawer-component ', [
        noTransition && 'no-transition',
        { noPadding },
        rootClassName,
      ])}
    >
      <div
        {...handlers}
        className={classNames('absolute', [
          placement && placementClassMapper[placement],
        ])}
      />
      {children}
    </AntDrawer>
  );
}
